import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Pagination,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { format, isValid, parseISO } from 'date-fns';
import { tr } from 'date-fns/locale';
import { adminApi } from '../services/apiService';
import { AdoptionHistoryRecord, AdoptionHistoryParams } from '../types';

const AdoptionHistory: React.FC = () => {
  const [data, setData] = useState<AdoptionHistoryRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Helper function to safely format dates
  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) {
      return 'Tarih belirtilmemiş';
    }
    
    try {
      const date = parseISO(dateString);
      if (isValid(date)) {
        return format(date, 'dd.MM.yyyy', { locale: tr });
      }
    } catch (error) {
      console.warn('Invalid date format:', dateString, error);
    }
    
    return 'Geçersiz tarih';
  };
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 0,
    currentPage: 1,
    limit: 20,
  });

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [adoptedSource, setAdoptedSource] = useState<'patigo' | 'other' | 'all'>('all');
  const [sortBy, setSortBy] = useState<'adoptedAt' | 'createdAt' | 'name'>('adoptedAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const fetchData = useCallback(async (params: AdoptionHistoryParams = {}) => {
    try {
      setLoading(true);
      setError(null);
      
      const queryParams: AdoptionHistoryParams = {
        page: pagination.currentPage,
        limit: pagination.limit,
        search: searchTerm || undefined,
        adoptedSource: adoptedSource !== 'all' ? adoptedSource : undefined,
        sortBy,
        sortOrder,
        ...params,
      };

      const response = await adminApi.getAdoptionHistory(queryParams);
      
      setData(response.adoptionHistory);
      setPagination(response.pagination);
    } catch (err) {
      setError('Sahiplenme geçmişi yüklenirken hata oluştu');
      console.error('Adoption history fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [pagination.currentPage, pagination.limit, searchTerm, adoptedSource, sortBy, sortOrder]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setPagination(prev => ({ ...prev, currentPage: page }));
  };

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    fetchData({ page: 1 });
  };

  const handleRefresh = () => {
    fetchData();
  };

  const handleFilterChange = (field: string, value: any) => {
    switch (field) {
      case 'adoptedSource':
        setAdoptedSource(value);
        break;
      case 'sortBy':
        setSortBy(value);
        break;
      case 'sortOrder':
        setSortOrder(value);
        break;
    }
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  const getSourceColor = (source: string) => {
    switch (source) {
      case 'patigo':
        return 'success';
      case 'other':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getSourceLabel = (source: string) => {
    switch (source) {
      case 'patigo':
        return 'Patigo';
      case 'other':
        return 'Diğer';
      default:
        return source;
    }
  };

  if (loading && data.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Typography variant="h5" component="h2">
              Sahiplenme Geçmişi
            </Typography>
            <IconButton onClick={handleRefresh} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Filters */}
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: { 
              xs: '1fr', 
              sm: '2fr 1fr 1fr 1fr',
              md: '2fr 1fr 1fr 1fr'
            }, 
            gap: 2, 
            mb: 3,
            alignItems: 'start'
          }}>
            <Box>
              <TextField
                fullWidth
                label="Pet Adı veya Irk Ara"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <IconButton onClick={handleSearch}>
                      <SearchIcon />
                    </IconButton>
                  ),
                }}
              />
            </Box>
            <Box>
              <FormControl fullWidth>
                <InputLabel>Sahiplenme Kaynağı</InputLabel>
                <Select
                  value={adoptedSource}
                  label="Sahiplenme Kaynağı"
                  onChange={(e) => handleFilterChange('adoptedSource', e.target.value)}
                >
                  <MenuItem value="all">Tümü</MenuItem>
                  <MenuItem value="patigo">Patigo</MenuItem>
                  <MenuItem value="other">Diğer</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box>
              <FormControl fullWidth>
                <InputLabel>Sıralama</InputLabel>
                <Select
                  value={sortBy}
                  label="Sıralama"
                  onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                >
                  <MenuItem value="adoptedAt">Sahiplenme Tarihi</MenuItem>
                  <MenuItem value="createdAt">Oluşturma Tarihi</MenuItem>
                  <MenuItem value="name">Pet Adı</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box>
              <FormControl fullWidth>
                <InputLabel>Sıralama Düzeni</InputLabel>
                <Select
                  value={sortOrder}
                  label="Sıralama Düzeni"
                  onChange={(e) => handleFilterChange('sortOrder', e.target.value)}
                >
                  <MenuItem value="desc">Azalan</MenuItem>
                  <MenuItem value="asc">Artan</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>

          {/* Table */}
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Pet Bilgileri</TableCell>
                  <TableCell>Sahiplenme Bilgileri</TableCell>
                  <TableCell>Sahip Bilgileri</TableCell>
                  <TableCell>Konum</TableCell>
                  <TableCell>Tarihler</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.map((record, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {record.pet.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {record.pet.type} - {record.pet.breed}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {record.pet.age} yaş - {record.pet.gender}
                        </Typography>
                        <Box sx={{ mt: 1 }}>
                          <Chip
                            label={record.pet.sterilizationStatus}
                            size="small"
                            color="info"
                            sx={{ mr: 1 }}
                          />
                          <Chip
                            label={record.pet.microchipStatus}
                            size="small"
                            color="secondary"
                          />
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Chip
                          label={getSourceLabel(record.adoption.adoptedSource)}
                          color={getSourceColor(record.adoption.adoptedSource)}
                          size="small"
                        />
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          {record.adoption.adoptedSourceDescription}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {record.owner.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {record.owner.email}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {record.owner.phone}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          {record.pet.location.city}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {record.pet.location.district}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {record.owner.city}, {record.owner.country}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          Sahiplenme: {formatDate(record.adoption.adoptedAt)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Kayıt: {formatDate(record.pet.createdAt)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Sahip Kayıt: {formatDate(record.owner.registeredAt)}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Pagination
                count={pagination.totalPages}
                page={pagination.currentPage}
                onChange={handlePageChange}
                color="primary"
                size="large"
              />
            </Box>
          )}

          {/* Summary */}
          <Box mt={2}>
            <Typography variant="body2" color="text.secondary">
              Toplam {pagination.total} sahiplenme kaydı gösteriliyor
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default AdoptionHistory;
